/**
 * Vendor flat-roof suitability response row (field names are stable; values vary).
 */
export interface VanDerValkSuitabilityConfig {
  /**
   * Stable configuration label from backend grouped payload keys (e.g. disambiguated
   * `ValkPro+ / L10 / EW / Landscape (2)`). When set, UI groups by this instead of `baseRowLabel`.
   */
  displayLabel?: string
  name: string
  system: string
  vandervalkExecution: string
  placeSystem: string
  moduleOrientation: string
  rowPitch?: string | number
  tilt?: string | number
  gap?: string | number
  dilatationMaxColumns?: string | number
  dilatationMaxRows?: string | number
  dilatationMaxColumnsLength?: string | number
  dilatationMaxRowsLength?: string | number
  frontPath?: string | number
  pvModuleLift?: string | number
  backPath?: string | number
  /** Pitched-roof (on-roof) only: sub-picker selections from the vendor pitched response. */
  color?: string
  fixation?: string
  longProfileType?: string
  profileLength?: string | number
  hangerBolt?: string
  /** Pitched roof-hook systems (e.g. MEMBRANE / VALK_PITCHED). */
  roofHookType?: string
  /** Vendor long profile code, e.g. ``L2370`` (sent as ``longProfiles`` array in BOM frame). */
  longProfile?: string
  screwType?: string
  /** ValkSolarFix (MEMBRANE): vendor range or user-selected scalar mm for BOM. */
  maxCtcDistance?: string | number | { min: number; max: number }
  /** ValkSolarFix: ``VALK_SOLAR_FIX`` or ``OTHER``. */
  anchorType?: string
  /** ValkSolarFix when anchorType is ``VALK_SOLAR_FIX``. */
  fastenerType?: string
  /** ValkSolarFix when anchorType is ``VALK_SOLAR_FIX``. */
  roofMaterial?: string
  /** ValkSolarFix when anchorType is ``VALK_SOLAR_FIX``; mm. */
  roofConstructionHeight?: string | number
  /** Standing seam clamp type (FLAT_CLAMP, STRAIGHT_CLAMP, ROUND_CLAMP). */
  seamClampType?: string
  /** Corrugated fiber-cement mounting bracket bolt size. */
  mountingBracketBoltSize?: string
  /** When vendor lists screw types on the fixation but not per flattened row. */
  availableScrewTypes?: string[]
  restrictPlacementToSubstructure?: boolean
  warnings: unknown[] | string[]
}

/** Module identity + mm dimensions used for suitability POST and cache invalidation. */
export interface VanDerValkSuitabilityModuleSpecs {
  id: string
  module_id: number | null
  code: string
  width: number
  length: number
  thickness: number
  /** Panel mass (kg) sent to vendor BOM; used for suitability cache invalidation. */
  weightKg: number
}

export type VanDerValkBomLeg = { bom: unknown } | { error: string }

export interface VanDerValkBomState {
  ballast: VanDerValkBomLeg | null
  fixation: VanDerValkBomLeg | null
}

export type VanDerValkSolarRampFoundationType = 'MASS_BLOCKS' | 'TILE_CARRIERS'
export type VanDerValkSolarRampFixation = 'BALLASTING' | 'ANCHORING'

export interface VanDerValkIntegrationState {
  suitabilityResult: VanDerValkSuitabilityConfig[]
  selectedSuitability: VanDerValkSuitabilityConfig | null
  suitabilityModuleSpecs?: VanDerValkSuitabilityModuleSpecs | null
  /**
   * Pitched-roof only: roof-derived ``claddingType`` suitability was fetched for.
   * Null for flat-roof. Used to invalidate stale results when the roof type or mounting type
   * changes.
   */
  suitabilityCladdingType?: string | null
  bom?: VanDerValkBomState | null
  middleZoneDeflector?: string
  /** Solar Ramp flat-roof only. */
  foundationType?: VanDerValkSolarRampFoundationType
  /** Solar Ramp flat-roof only (not pitched ``fixation`` on suitability rows). */
  flatRoofFixation?: VanDerValkSolarRampFixation
  /** Solar Ramp anchoring sub-fields (integration_json; when ``flatRoofFixation`` is ``ANCHORING``). */
  anchorType?: 'VALK_SOLAR_FIX' | 'OTHER'
  fastenerType?: string
  roofMaterial?: string
  roofConstructionHeight?: string | number
  /** MCS UK topography; server derives vendor ``orographyFactor``. */
  topography_type?: string
  topography_zone?: string
  topography_slope?: string
  /** Pitched-roof BOM: user cladding sub-options keyed by vendor field name. */
  pitchedCladdingOptions?: Record<string, string | number>
  /** Roof-derived cladding type the stored ``pitchedCladdingOptions`` belong to. */
  pitchedCladdingOptionsCladdingType?: string
  /** Pitched STANDING_SEAM only: vendor ``seamType`` used for the last suitability fetch. */
  suitabilitySeamType?: string | null
  /** Pitched only: bump when backend row flattening changes (cache invalidation). */
  suitabilityGroupingVersion?: number
  /** Pitched only: cache key for ``claddingOptions`` sent with the last suitability fetch. */
  suitabilityCladdingOptionsKey?: string | null
}

export function isVanDerValkSuitabilityConfigRow(x: unknown): x is VanDerValkSuitabilityConfig {
  if (x == null || typeof x !== 'object') return false
  const o = x as Record<string, unknown>
  if (typeof o.name !== 'string' || typeof o.system !== 'string') return false
  if (typeof o.vandervalkExecution !== 'string') return false
  if (typeof o.placeSystem !== 'string') return false
  const mo = o.moduleOrientation ?? o.ModuleOrientation
  if (typeof mo !== 'string') return false
  return true
}

/** Coerce parsed JSON into a config (defaults for optional vendor fields). */
export function normalizeVanDerValkSuitabilityRow(o: VanDerValkSuitabilityConfig): VanDerValkSuitabilityConfig {
  const r = o as unknown as Record<string, unknown>
  const displayLabel = o.displayLabel ?? r.displayLabel
  return {
    ...(typeof displayLabel === 'string' && displayLabel.trim() !== '' ? { displayLabel: displayLabel.trim() } : {}),
    name: o.name,
    system: o.system,
    vandervalkExecution: o.vandervalkExecution,
    placeSystem: o.placeSystem,
    moduleOrientation: String(o.moduleOrientation ?? r.ModuleOrientation ?? ''),
    rowPitch: o.rowPitch,
    tilt: (o.tilt ?? r.Tilt) as VanDerValkSuitabilityConfig['tilt'],
    gap: (o.gap ?? r.Gap) as VanDerValkSuitabilityConfig['gap'],
    dilatationMaxColumns: o.dilatationMaxColumns,
    dilatationMaxRows: o.dilatationMaxRows,
    dilatationMaxColumnsLength: o.dilatationMaxColumnsLength,
    dilatationMaxRowsLength: o.dilatationMaxRowsLength,
    frontPath: o.frontPath,
    pvModuleLift: (o.pvModuleLift ?? r.PvModuleLift) as VanDerValkSuitabilityConfig['pvModuleLift'],
    backPath: o.backPath,
    color: o.color,
    fixation: o.fixation,
    longProfileType: o.longProfileType,
    profileLength: o.profileLength,
    hangerBolt: o.hangerBolt,
    roofHookType: (o.roofHookType ?? r.roofHookType) as VanDerValkSuitabilityConfig['roofHookType'],
    longProfile: (o.longProfile ?? r.longProfile) as VanDerValkSuitabilityConfig['longProfile'],
    screwType: (o.screwType ?? r.screwType) as VanDerValkSuitabilityConfig['screwType'],
    maxCtcDistance: (o.maxCtcDistance ?? r.maxCtcDistance) as VanDerValkSuitabilityConfig['maxCtcDistance'],
    anchorType: (o.anchorType ?? r.anchorType) as VanDerValkSuitabilityConfig['anchorType'],
    fastenerType: (o.fastenerType ?? r.fastenerType) as VanDerValkSuitabilityConfig['fastenerType'],
    roofMaterial: (o.roofMaterial ?? r.roofMaterial) as VanDerValkSuitabilityConfig['roofMaterial'],
    roofConstructionHeight: (o.roofConstructionHeight ?? r.roofConstructionHeight) as VanDerValkSuitabilityConfig['roofConstructionHeight'],
    seamClampType: (o.seamClampType ?? r.seamClampType) as VanDerValkSuitabilityConfig['seamClampType'],
    mountingBracketBoltSize: (o.mountingBracketBoltSize ?? r.mountingBracketBoltSize) as VanDerValkSuitabilityConfig['mountingBracketBoltSize'],
    availableScrewTypes: (o.availableScrewTypes ?? r.availableScrewTypes) as VanDerValkSuitabilityConfig['availableScrewTypes'],
    restrictPlacementToSubstructure:
      typeof o.restrictPlacementToSubstructure === 'boolean'
        ? o.restrictPlacementToSubstructure
        : typeof r.restrictPlacementToSubstructure === 'boolean'
          ? r.restrictPlacementToSubstructure
          : undefined,
    warnings: Array.isArray(o.warnings) ? o.warnings : [],
  }
}
